import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import LoadingScreen from "@/components/LoadingScreen";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { format } from "date-fns";
import { ArrowLeft, MessageSquare } from "lucide-react";
import { UserAvatar } from "@/components/UserAvatar";

interface Participant {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
}

interface Message {
  id: string;
  text: string;
  sender_id: string;
  created_at: string;
  is_read: boolean;
  file_url?: string | null;
  file_name?: string | null;
}

interface ConversationData {
  id: string;
  participant1_id: string;
  participant2_id: string;
  service_id: string | null;
  service_completed: boolean;
  payment_completed: boolean;
  final_price: number | null;
}

const AdminConversationView = () => {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, loading: roleLoading } = useUserRole();
  
  const [conversation, setConversation] = useState<ConversationData | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [participant1, setParticipant1] = useState<Participant | null>(null);
  const [participant2, setParticipant2] = useState<Participant | null>(null);
  const [providerId, setProviderId] = useState<string | null>(null);
  const [serviceTitle, setServiceTitle] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isAdmin && conversationId) {
      loadConversation();
    }
  }, [isAdmin, conversationId]);

  const loadConversation = async () => {
    if (!conversationId) return;
    
    setLoading(true);
    try {
      // Fetch conversation
      const { data: convo, error: convoError } = await supabase
        .from('conversations')
        .select('*')
        .eq('id', conversationId)
        .single();

      if (convoError) throw convoError;
      setConversation(convo);

      // Fetch participant profiles
      const [p1Result, p2Result] = await Promise.all([
        supabase.rpc('get_public_profile', { profile_user_id: convo.participant1_id }),
        supabase.rpc('get_public_profile', { profile_user_id: convo.participant2_id })
      ]);

      setParticipant1(p1Result.data?.[0] || null);
      setParticipant2(p2Result.data?.[0] || null);

      // Determine who is the provider (service owner)
      if (convo.service_id) {
        const { data: service } = await supabase
          .from('services')
          .select('user_id, title')
          .eq('id', convo.service_id)
          .single();
        
        if (service) {
          setProviderId(service.user_id);
          setServiceTitle(service.title);
        }
      }

      // Fetch messages
      const { data: msgs, error: msgsError } = await supabase
        .from('messages')
        .select('*')
        .eq('conversation_id', conversationId)
        .order('created_at', { ascending: true });

      if (msgsError) throw msgsError;
      setMessages(msgs || []);

    } catch (error) {
      console.error("Failed to load conversation:", error);
      toast.error("Failed to load conversation");
    } finally {
      setLoading(false);
    }
  };

  const getParticipantName = (participant: Participant | null) => {
    if (!participant) return "Unknown User";
    return `${participant.first_name || ''} ${participant.last_name || ''}`.trim() || "Unknown User";
  };

  const getParticipantInitials = (participant: Participant | null) => {
    if (!participant) return "U";
    return `${participant.first_name?.[0] || ''}${participant.last_name?.[0] || ''}`.toUpperCase() || "U";
  };

  if (roleLoading) {
    return <LoadingScreen />;
  }

  if (!user || !isAdmin) {
    return <Navigate to="/" replace />;
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading conversation...</p>
      </div>
    );
  }

  if (!conversation) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4">
        <p className="text-muted-foreground">Conversation not found</p>
        <Button onClick={() => navigate('/admin/conversations')}>
          Back to Conversations
        </Button>
      </div>
    );
  }

  // Determine provider and client
  const isP1Provider = providerId === conversation.participant1_id;
  const provider = isP1Provider ? participant1 : participant2;
  const client = isP1Provider ? participant2 : participant1;
  const providerUserId = isP1Provider ? conversation.participant1_id : conversation.participant2_id;
  const clientUserId = isP1Provider ? conversation.participant2_id : conversation.participant1_id;

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="container mx-auto px-4 max-w-5xl">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin/conversations')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <MessageSquare className="h-6 w-6" />
              Conversation View
            </h1>
            {serviceTitle && (
              <p className="text-muted-foreground">
                Service: <span className="font-medium">{serviceTitle}</span>
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {conversation.service_completed && (
              <Badge className="bg-green-100 text-green-700">Completed</Badge>
            )}
            {conversation.payment_completed && (
              <Badge className="bg-blue-100 text-blue-700">Paid</Badge>
            )}
            {conversation.final_price && (
              <Badge variant="outline">${conversation.final_price}</Badge>
            )}
          </div>
        </div>

        {/* Participant headers */}
        <div className="grid grid-cols-2 gap-4 mb-4">
          <Card className="border-l-4 border-l-primary">
            <CardContent className="p-4 flex items-center gap-3">
              <UserAvatar
                userId={providerUserId}
                avatarUrl={provider?.avatar_url}
                fallback={getParticipantInitials(provider)}
                size="md"
                showLevel={false}
              />
              <div>
                <p className="font-semibold text-foreground">{getParticipantName(provider)}</p>
                <p className="text-xs text-primary font-medium">Provider</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-r-4 border-r-secondary">
            <CardContent className="p-4 flex items-center gap-3 justify-end text-right">
              <div>
                <p className="font-semibold text-foreground">{getParticipantName(client)}</p>
                <p className="text-xs text-secondary-foreground font-medium">Client</p>
              </div>
              <UserAvatar
                userId={clientUserId}
                avatarUrl={client?.avatar_url}
                fallback={getParticipantInitials(client)}
                size="md"
                showLevel={false}
              />
            </CardContent>
          </Card>
        </div>

        {/* Messages */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm text-muted-foreground">
              {messages.length} messages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[60vh]">
              <div className="space-y-4 p-2">
                {messages.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8">
                    No messages in this conversation
                  </p>
                ) : (
                  messages.map((msg) => {
                    const isProvider = msg.sender_id === providerUserId;
                    const sender = isProvider ? provider : client;
                    
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isProvider ? 'justify-start' : 'justify-end'}`}
                      >
                        <div
                          className={`flex gap-2 max-w-[70%] ${
                            isProvider ? 'flex-row' : 'flex-row-reverse'
                          }`}
                        >
                          <UserAvatar
                            userId={msg.sender_id}
                            avatarUrl={sender?.avatar_url}
                            fallback={getParticipantInitials(sender)}
                            size="sm"
                            showLevel={false}
                          />
                          <div
                            className={`rounded-lg p-3 ${
                              isProvider
                                ? 'bg-primary/10 rounded-tl-none'
                                : 'bg-secondary/20 rounded-tr-none'
                            }`}
                          >
                            <p className="text-xs text-muted-foreground mb-1">
                              {format(new Date(msg.created_at), 'MMM d, h:mm a')}
                            </p>
                            <p className="text-sm text-foreground break-words">
                              {msg.text.startsWith('[') ? (
                                <span className="italic text-muted-foreground">
                                  {msg.text}
                                </span>
                              ) : (
                                msg.text
                              )}
                            </p>
                            {msg.file_url && (
                              <a
                                href={msg.file_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-primary hover:underline block mt-1"
                              >
                                📎 {msg.file_name || 'Attachment'}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminConversationView;
